/**
 * Keeps OneSignal External ID and contact subscriptions in sync with the logged-in artist.
 */

import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import {
  logOutOneSignal,
  requestNotificationPermission,
  syncOneSignalUser,
} from '@/services/oneSignal';
import { resolveArtistSlug } from '@/utils/resolveArtistSlug';

export function useOneSignalSession(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const artistDetails = useArtistDetailsStore((s) => s.data);

  useEffect(() => {
    if (!isAuthenticated) {
      void logOutOneSignal();
      return;
    }

    void (async () => {
      const artistId = await resolveArtistSlug(artistDetails, user);

      if (!artistId) {
        console.warn(
          '[OneSignal] Skipping user sync — artist slug not found. Push notifications will not be delivered until OneSignal.login(slug) succeeds.',
          {
            userId: user?.id ?? null,
            artistSlug: artistDetails?.user_info?.artist_slug ?? null,
            hasArtistDetails: Boolean(artistDetails),
          },
        );
        return;
      }

      let details = artistDetails;
      let email = details?.user_info?.email?.trim() || user?.email?.trim();
      let phone = details?.user_info?.phone?.trim() || user?.phone?.trim();

      if (!email || !phone) {
        details =
          details ?? (await useArtistDetailsStore.getState().ensureFetched());
        email = details?.user_info?.email?.trim() || user?.email?.trim();
        phone = details?.user_info?.phone?.trim() || user?.phone?.trim();

        if (!email || !phone) {
          details =
            (await useArtistDetailsStore.getState().fetchArtistDetails()) ??
            details;
          email = details?.user_info?.email?.trim() || user?.email?.trim();
          phone = details?.user_info?.phone?.trim() || user?.phone?.trim();
        }
      }

      await syncOneSignalUser({
        artistId,
        email: email || undefined,
        phone: phone || undefined,
      });

      await requestNotificationPermission();
    })();
  }, [
    isAuthenticated,
    artistDetails?.user_info?.artist_slug,
    artistDetails?.user_info?.email,
    artistDetails?.user_info?.phone,
    user?.id,
    user?.email,
    user?.phone,
  ]);
}
